import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { useLivePageContent } from '@/hooks/usePageContent';
import { ecommerceService as svc } from '@/services/ecommerce.service';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { ActiveSearchChips, normText } from '@/components/public/ActiveSearchChips';
import { FavoriteButton } from '@/components/public/FavoriteButton';
import { CetsTrees } from '@/components/CetsTrees';
import { cetsSelfService } from '@/services/cets.service';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, Store, ArrowRight, ImageIcon, Package, Search, Tag } from 'lucide-react';

const HERO = 'https://images.pexels.com/photos/821365/pexels-photo-821365.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920';
const SLUG = 'shop';
const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
const eur = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

function ShopCard({ s, rating }) {
  return (
    <Link
      to={`/negozio/${s.owner_id}`}
      className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`shop-card-${s.owner_id}`}
    >
      <div className="relative h-52 overflow-hidden bg-muted">
        <FavoriteButton structureId={s.owner_id} structureType="shop" className="absolute left-3 top-3 z-10" />
        {rating && <div className="absolute bottom-3 left-3 z-10"><CetsTrees rating={rating} ownerId={s.owner_id} size="sm" showLabel={false} /></div>}
        {s.cover_image ? (
          <img src={mediaUrl(s.cover_image)} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-9 w-9 text-muted-foreground" /></div>
        )}
        {s.from_price != null && (
          <span className="absolute right-3 top-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground backdrop-blur">da {eur(s.from_price)}</span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-semibold text-foreground">{s.name}</h3>
        {s.tagline && <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{s.tagline}</p>}
        {(s.categories || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {s.categories.slice(0, 4).map((c) => <Badge key={c} variant="secondary" className="font-normal">{c}</Badge>)}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
          <span className="flex items-center gap-1.5 text-sm text-foreground"><Package className="h-4 w-4 text-primary" />{s.product_count} prodott{s.product_count === 1 ? 'o' : 'i'}</span>
          <span className="flex items-center gap-1 text-sm font-medium text-primary">Entra <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function Shop() {
  const [items, setItems] = useState(null);
  const ov = useLivePageContent(SLUG);
  const [ratings, setRatings] = useState({});
  const [params, setParams] = useSearchParams();
  const loc = params.get('loc') || '';
  const cat = params.get('cat') || '';

  useEffect(() => {
    (async () => {
      try { const data = await svc.publicShops(); setItems(data || []); }
      catch (e) { setItems([]); }
    })();
  }, []);

  useEffect(() => {
    const ids = (items || []).map((s) => s.owner_id).filter(Boolean);
    if (!ids.length) return;
    cetsSelfService.publicRatings(ids).then((m) => setRatings(m || {})).catch(() => {});
  }, [items]);

  const allCats = useMemo(() => {
    const map = new Map();
    (items || []).forEach((s) => (s.categories || []).forEach((c) => {
      if (!c) return;
      map.set(c, (map.get(c) || 0) + 1);
    }));
    return Array.from(map.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [items]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (cat) list = list.filter((s) => (s.categories || []).some((c) => c === cat));
    if (loc) {
      const q = normText(loc);
      list = list.filter((s) => normText([s.name, s.tagline, (s.categories || []).join(' ')].join(' ')).includes(q));
    }
    return list;
  }, [items, loc, cat]);

  const setCat = (c) => {
    const p = new URLSearchParams(params);
    if (c) p.set('cat', c); else p.delete('cat');
    setParams(p, { replace: true });
  };

  const chips = [
    ...(cat ? [{ icon: Tag, text: cat }] : []),
    ...(loc ? [{ icon: Search, text: loc }] : []),
  ];
  const clearFilters = () => setParams({}, { replace: true });

  const heroImage = str(ov.heroImage) || HERO;
  const eyebrow = str(ov.eyebrow) || 'Shop';
  const title = str(ov.title) || 'La bottega dei prodotti della Sila';
  const lead = str(ov.lead) || 'Prodotti tipici, bio e a km.0 realizzati dai produttori locali. Scegli un negozio ed esplora la sua vetrina.';
  const intro = str(ov.intro);

  return (
    <PublicLayout>
      <Seo title="Shop — Sila Experiences" description={lead} image={heroImage} />

      <SilaHero image={heroImage} badge={eyebrow} title={title} subtitle={lead} icon={ShoppingBag} />

      {intro && (
        <section className="sila-section pt-12" data-testid="shop-intro">
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">{intro}</p>
        </section>
      )}

      {allCats.length > 0 && (
        <section className="sila-section pt-10" data-testid="shop-categories">
          <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Sfoglia per categoria</h2>
          <p className="mt-1 text-sm text-muted-foreground">Scegli una categoria per trovare subito i prodotti che cerchi.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={() => setCat('')} data-testid="shop-cat-all"
              className={`flex items-center gap-2.5 rounded-2xl border px-4 py-3 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${!cat ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
              <span className={`flex h-9 w-9 items-center justify-center rounded-full ${!cat ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}><Store className="h-4 w-4" /></span>
              <span className="min-w-0"><span className="block text-sm font-semibold text-foreground">Tutte</span><span className="block text-xs text-muted-foreground">{(items || []).length} negozi</span></span>
            </button>
            {allCats.map((c) => (
              <button key={c.name} type="button" onClick={() => setCat(c.name)} data-testid={`shop-cat-${c.name}`}
                className={`flex items-center gap-2.5 rounded-2xl border px-4 py-3 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${cat === c.name ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
                <span className={`flex h-9 w-9 items-center justify-center rounded-full ${cat === c.name ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}><Tag className="h-4 w-4" /></span>
                <span className="min-w-0"><span className="block text-sm font-semibold text-foreground">{c.name}</span><span className="block text-xs text-muted-foreground">{c.count} negoz{c.count === 1 ? 'io' : 'i'}</span></span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="sila-section py-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">I negozi</h2>
        <p className="mt-1 text-sm text-muted-foreground">Clicca su un negozio per aprirne la vetrina e vedere i prodotti disponibili.</p>

        <div className="mt-6">
          <ActiveSearchChips chips={chips} onClear={clearFilters} resultCount={filtered.length} noun="negozi" />
          {items === null ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="shop-empty">
              <Store className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">
                {cat ? `Nessun negozio nella categoria “${cat}”` : loc ? `Nessun negozio trovato per “${loc}”` : 'Nessun negozio disponibile al momento'}
              </p>
              <p className="text-sm text-muted-foreground">
                {(cat || loc) ? 'Prova a rimuovere i filtri per vedere tutti i negozi.' : 'I nuovi negozi appariranno qui non appena pubblicheranno i loro prodotti.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-grid">
              {filtered.map((s) => <ShopCard key={s.owner_id} s={s} rating={ratings[s.owner_id]} />)}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
